import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Volume2, BookmarkPlus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorText } from "@/lib/errorText";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { fixedLabel, FormsSection, FamilySection, UsageSection, TrapsSection, ExamplesSection, ProLock } from "@/components/WordExplorerSections";

const clips = new Map(); // session memory: repeat Listen never hits the server

// Deep-linkable: /vocab/explore?q=<word> — future Lens/Today/Friend/Journal links pass only the word.
export default function WordExplorer() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const vocabId = params.get("vocab");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q) return;
    setData(null); setError(null);
    api.get("/words/explore", { params: vocabId ? { q, vocab_id: vocabId } : { q } })
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e, t, "wordExplorer.failed")));
  }, [q, vocabId, t, user?.target_script]);

  const more = async () => {
    setMoreBusy(true);
    try { const r = await api.post(`/words/explore/${data.entry_id}/more`, null, { params: { q } }); setData(r.data); }
    catch (e) { toast.error(errorText(e, t, "wordExplorer.failed")); }
    setMoreBusy(false);
  };
  const save = async () => {
    setSaving(true);
    try { await api.post(`/words/explore/${data.entry_id}/save`); setData({ ...data, is_saved: true }); toast.success(t("wordExplorer.saved")); }
    catch (e) { toast.error(errorText(e, t, "wordExplorer.failed")); }
    setSaving(false);
  };
  const [voiceFailed, setVoiceFailed] = useState(false);
  const listen = async (form = "lemma") => {
    const id = `${data.entry_id}|${form}|${form === "searched" ? data.query.toLowerCase() : ""}`;
    setVoiceFailed(false);
    try {
      if (!clips.has(id)) {
        const r = await api.post(`/words/explore/${data.entry_id}/audio`, null,
          { params: form === "searched" ? { form, q: data.query } : { form }, responseType: "blob" });
        clips.set(id, URL.createObjectURL(r.data));
      }
      await new Audio(clips.get(id)).play();
    } catch { setVoiceFailed(true); }
  };

  const dir = data?.dir || "auto";
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="word-explorer">
        <Link to="/vocab" className="text-sm text-gray-500 hover:text-ink inline-flex items-center gap-1" data-testid="wx-back"><ArrowLeft className="w-4 h-4" /> {t("wordExplorer.back")}</Link>
        {!data && !error && <p className="mt-10 text-gray-500 flex items-center gap-2" data-testid="wx-loading"><Loader2 className="w-4 h-4 animate-spin" /> {t("wordExplorer.loading")}</p>}
        {error && <p className="mt-10 text-coral font-bold" data-testid="wx-error">{error}</p>}
        {data && (
          <>
            <header className="mt-6">
              <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("wordExplorer.title")}</div>
              <div className="flex items-center gap-3 mt-1">
                <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl" dir={dir} data-testid="wx-lemma">{data.lemma}</h1>
                {data.can_listen && <button onClick={() => listen()} className="text-gray-400 hover:text-ink" title={t("wordExplorer.listen")} data-testid="wx-listen"><Volume2 className="w-5 h-5" /></button>}
              </div>
              {voiceFailed && <p className="text-xs text-gray-500 mt-1" data-testid="wx-voice-unavailable">{t("friend.live.voiceUnavailable")}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
                {data.pos && <span className="nb-tag bg-paper" data-testid="wx-pos">{fixedLabel(t, "pos", data.pos)}</span>}
                {data.gender && <span className="nb-tag bg-paper">{data.gender}</span>}
                {data.target_lang === "sr" && (
                  <Link to="/settings#serbian-script" className="text-xs text-gray-500 hover:text-ink underline" data-testid="wx-script-indicator">
                    {t("wordExplorer.script")}: {data.script === "latin" ? "Latinica" : "Ћирилица"}
                  </Link>
                )}
              </div>
              <p className="text-lg mt-3" data-testid="wx-meaning">{data.meaning}</p>
              {data.alt_meanings?.length > 0 && <p className="text-sm text-gray-500 mt-1" data-testid="wx-alt">{t("wordExplorer.alsoMeans")}: {data.alt_meanings.join(" · ")}</p>}
              {data.searched_form_note && (
                <p className="text-sm text-gray-600 mt-3 bg-paper rounded-lg px-3 py-2" data-testid="wx-searched-note">
                  <span dir={dir} className="font-bold">{data.query}</span>
                  {data.can_listen_searched && <button onClick={() => listen("searched")} className="inline-flex align-middle mx-1 text-gray-400 hover:text-ink" title={t("wordExplorer.listen")} data-testid="wx-listen-searched"><Volume2 className="w-4 h-4" /></button>}
                  — {data.searched_form_note}
                </p>
              )}
              {data.register_note && <p className="text-xs text-gray-500 mt-2">{data.register_note}</p>}
              <button onClick={save} disabled={saving || data.is_saved} className={`${data.is_saved ? "nb-btn-mint" : "nb-btn-primary"} mt-5 px-4 py-2 text-sm inline-flex`} data-testid="wx-save">
                {data.is_saved ? <><Check className="w-4 h-4" /> {t("wordExplorer.saved")}</> : <><BookmarkPlus className="w-4 h-4" /> {t("wordExplorer.save")}</>}
              </button>
            </header>
            <FormsSection forms={data.forms} dir={dir} t={t} />
            <FamilySection family={data.family} dir={dir} t={t} />
            <UsageSection usage={data.usage} dir={dir} t={t} />
            <TrapsSection traps={data.traps} t={t} />
            <ExamplesSection examples={data.examples} dir={dir} t={t} />
            {data.locked && <ProLock t={t} />}
            {data.has_more && (
              <button onClick={more} disabled={moreBusy} className="nb-btn-outline mt-8 px-4 py-2 text-sm inline-flex" data-testid="wx-more-btn">
                {moreBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("wordExplorer.loading")}</> : t("wordExplorer.more")}
              </button>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
